<template>
  <div class="cs-table-input" :style="`width:${width}`">
    <el-button
      v-if="tableData.length === 0 && !disabled"
      size="mini"
      style="margin-top: 8px"
      icon="el-icon-circle-plus-outline"
      @click="onAddRow"
      >{{ addText }}</el-button
    >
    <el-form
      ref="form"
      inline-message
      size="mini"
      :model="{ tableData: tableData }"
      label-width="0"
    >
      <transition
        name="slide"
        @enter="enter"
        @afterEnter="afterEnter"
        @leave="leave"
        @afterLeave="afterLeave"
        @beforeLeave="beforeLeave"
      >
        <el-table
          v-show="tableData.length > 0 || disabled"
          size="mini"
          border
          :data="tableData"
          :row-key="rowKey"
          style="width: 100%"
        >
          <el-table-column
            v-for="column in _columns"
            :key="column.dataIndex"
            :label="column.label"
            :prop="column.dataIndex"
            :formatter="column.formatter"
            :width="column.width"
          >
            <template v-if="column.type" v-slot="scope">
              <el-form-item
                class="cs-table-input-item"
                :prop="`tableData.${scope.$index}.${column.dataIndex}`"
                :rules="handleColumnValidate(column.validate, scope)"
              >
                <edit-input
                  v-if="dealType(column.type, scope.row) === 'input'"
                  :placeholder="column.placeholder"
                  :clearable="column.clearable"
                  v-model="scope.row[column.dataIndex]"
                  :disabled="dealDisabled(column.disabled, scope.row)"
                  @change="setModified(scope.row, column, scope.$index)"
                ></edit-input>
                <edit-select
                  :ref="column.dataIndex"
                  :row="scope.row"
                  :placeholder="column.placeholder"
                  :multiple="column.multiple"
                  v-if="dealType(column.type, scope.row) === 'select'"
                  v-model="scope.row[column.dataIndex]"
                  :clearable="column.clearable"
                  :filterable="column.filterable"
                  :options="column.options"
                  :disabled="dealDisabled(column.disabled, scope.row)"
                  @change="setModified(scope.row, column, scope.$index)"
                ></edit-select>
                <edit-input-number
                  v-if="dealType(column.type, scope.row) === 'inputNumber'"
                  v-model="scope.row[column.dataIndex]"
                  :placeholder="column.placeholder"
                  :precision="column.precision"
                  :step-strictly="column.stepStrictly"
                  :step="column.step"
                  :min="column.min"
                  :max="column.max"
                  :disabled="dealDisabled(column.disabled, scope.row)"
                  @change="setModified(scope.row, column, scope.$index)"
                ></edit-input-number>
                <edit-time-select
                  :placeholder="column.placeholder"
                  v-if="dealType(column.type, scope.row) === 'timeSelect'"
                  v-model="scope.row[column.dataIndex]"
                  :disabled="dealDisabled(column.disabled, scope.row)"
                  :pickerOptions="column.pickerOptions"
                  :clearable="column.clearable"
                  @change="setModified(scope.row, column, scope.$index)"
                ></edit-time-select>
                <edit-between-number
                  :placeholder="column.placeholder"
                  v-if="dealType(column.type, scope.row) === 'betweenNumber'"
                  v-model="scope.row[column.dataIndex]"
                  :disabled="dealDisabled(column.disabled, scope.row)"
                  :min="column.min"
                  :max="column.max"
                  :precision="column.precision"
                  @change="setModified(scope.row, column, scope.$index)"
                ></edit-between-number>
              </el-form-item>
            </template>
          </el-table-column>
          <el-table-column
            label="操作"
            align="center"
            :width="operationWidth"
            v-if="showOperations"
          >
            <template v-slot="scope">
              <el-button
                v-for="(item, index) in operations"
                :key="index"
                type="text"
                size="mini"
                :disabled="dealDisabled(item.disabled, scope.row)"
                @click="() => dealAction(item.name, item.action, scope)"
                >{{ item.name | nameFilter }}</el-button
              >
            </template>
          </el-table-column>
        </el-table>
      </transition>
    </el-form>
  </div>
</template>
<script>
import EditSelect from "./types/select.vue";
import EditInput from "./types/input.vue";
import EditInputNumber from "./types/input-number.vue";
import EditTimeSelect from "./types/time-select.vue";
import EditBetweenNumber from "./types/between-number.vue";
import emitter from "ccxd-ux/src/mixins/emitter";
let count = 0;
export default {
  name: "CsTableInput",
  mixins: [emitter],
  components: {
    EditSelect,
    EditInput,
    EditInputNumber,
    EditTimeSelect,
    EditBetweenNumber,
  },
  model: {
    prop: "tableData",
    event: "change",
  },
  props: {
    addText: {
      type: String,
      required: false,
      default: "添加",
    },
    width: {
      type: String,
      required: false,
      default: "100%",
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    columns: {
      type: Array,
      required: true,
    },
    tableData: {
      type: Array,
      required: true,
    },
    operations: {
      type: Array,
      required: false,
      default() {
        return [
          {
            name: "add",
            action: undefined,
          },
          {
            name: "delete",
            action: undefined,
          },
        ];
      },
    },
  },
  watch: {
    tableData: {
      handler(n) {
        this.$emit("change", n);
      },
      deep: true,
    },
  },
  filters: {
    nameFilter(name) {
      const { [name]: text } = { add: "添加", delete: "删除" };
      return text;
    },
  },
  computed: {
    _columns() {
      return this.columns.filter((item) => item.visible !== false);
    },
    showOperations() {
      return !this.disabled && this.operations.length > 0;
    },
    operationWidth() {
      if (this.operations.length === 1) {
        return 58;
      } else if (this.operations.length === 2) {
        return 84;
      }
      return 0;
    },
    parent() {
      let parent = this.$parent;
      while (parent && parent.$options.name !== "CsForm") {
        parent = parent.$parent;
      }
      return parent;
    },
  },
  mounted() {
    this.parent?.addValidateRules?.(this.validate);
    this.$once("hook:beforeDestroy", () => {
      this.parent?.removeValidateRules?.(this.validate);
    });
  },
  methods: {
    handleColumnValidate(validate, scope) {
      if (typeof validate === "function") {
        return validate(scope);
      } else {
        return validate;
      }
    },
    validate() {
      return new Promise((resolve) => {
        this.$refs.form.validate(function (flag) {
          resolve(flag);
        });
      });
    },
    dealType(type, row) {
      if (typeof type === "function") {
        return type(row);
      } else {
        return type;
      }
    },
    dealDisabled(disabled, row) {
      if (this.disabled) {
        return true;
      }
      if (typeof disabled === "function") {
        return disabled(row);
      } else {
        return disabled;
      }
    },
    dealAction(name, action, scope) {
      if (name === "add") {
        if (action) {
          action(scope, this.tableData);
        } else {
          this.onAddRow(scope);
        }
      } else if (name === "delete") {
        if (action) {
          action(scope, this.tableData);
        } else {
          this.onDeleteRow(scope);
        }
      }
    },
    onDeleteRow(scope) {
      this.tableData.splice(scope.$index, 1);
      this.dispatch("ElFormItem", "el.form.blur");
    },
    onAddRow(scope) {
      const row = {
        _isNew: true,
        _id: this.createId(),
      };
      this.columns.forEach((item) => {
        if (item.multiple === true || item.type === "between") {
          row[item.dataIndex] = [];
        } else {
          row[item.dataIndex] = undefined;
        }
      });
      this.tableData.splice(scope.$index + 1, 0, row);
      this.dispatch("ElFormItem", "el.form.blur");
    },
    createId() {
      return "id_" + count++;
    },
    rowKey(row) {
      return row._id;
    },
    setModified(row, column, rowIndex) {
      if (column.onChange) {
        const instanceObject = {};
        this.columns.forEach((item) => {
          const instance = this.$refs[item.dataIndex]?.[rowIndex];
          if (instance) {
            if (instance.setOptions) {
              instanceObject[item.dataIndex] = {
                setOptions: instance.setOptions,
              };
            }
          }
        });
        column.onChange(row, instanceObject);
      }
      if (row._isNew === true) {
        return;
      }
      if (row._isModified === true) {
        return;
      }
      this.$set(row, "_isModified", true);
    },
    enter(el) {
      this.$nextTick(function () {
        el.dataset.oldOverflow = el.style.overflow;
        if (el.scrollHeight !== 0) {
          el.style.height = el.scrollHeight + "px";
        } else {
          el.style.height = "";
        }

        el.style.overflow = "hidden";
      });
    },
    afterEnter(el) {
      el.style.height = "";
      el.style.overflow = el.dataset.oldOverflow;
    },
    beforeLeave(el) {
      el.dataset.oldOverflow = el.style.overflow;
      el.style.height = el.scrollHeight + "px";
      el.style.overflow = "hidden";
    },
    leave(el) {
      if (el.scrollHeight !== 0) {
        el.style.height = 0;
      }
    },
    afterLeave(el) {
      el.style.height = "";
      el.style.overflow = el.dataset.oldOverflow;
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .el-table--mini td {
    padding: 0;
    vertical-align: top;
  }
  .el-form-item.is-error {
    margin-bottom: -6px !important;
  }
  .el-form-item__content,
  .cell {
    line-height: 24px;
  }
  .cell {
    padding: 6px 10px;
  }
  .el-form-item__error--inline {
    margin-left: 0;
  }
  .cs-table-input-item .el-input__inner,
  .cs-table-input-item .el-input__inner:focus {
    border-color: #d0d2d7 !important;
  }
  .cs-table-input-item.is-error .el-input__inner,
  .cs-table-input-item.is-error .el-input__inner:focus {
    border-color: #f95f5b !important;
  }
}
.el-form-item--mini.el-form-item {
  margin-bottom: 0;
}
.cs-table-input {
  .slide-enter-active,
  .slide-leave-active {
    transition: all 0.3s;
  }
  .slide-enter,
  .slide-leave-to {
    height: 0;
  }
}
</style>
